import {
  Application,
  Container,
  Graphics,
  Sprite,
  Assets,
  Texture,
  BlurFilter,
  Text,
  TextStyle,
} from "pixi.js";
import { ClientPokemon } from "@/lib/battle/entities/client-pokemon";
import { VFX } from "@/lib/battle/entities/client-vfx";
import { DuelPokemon } from "types/duel-dynamodb";
import {
  GRID_WIDTH,
  GRID_HEIGHT,
  gridTileSize,
  gridMarginX,
  gridMarginY,
} from "types/battle-engine/constants";
import { Direction, PokemonType } from "types/battle-engine/battle";

const VFX_SELECTED = "/assets/spells/self/self_sparkles_006__50.png";
const VFX_PICKED = "/assets/spells/self/powerup-1.png";
const VFX_BANNED = "/assets/spells/impact/fanfx1_skull_puff_large_white.png";

// Small gap offset to visually separate pokemons during draft
const DRAFT_GAP_OFFSET = 32;
// Extra vertical offset to push teams away from center (creates space for UI)
const DRAFT_VERTICAL_SPREAD = 48;

// Platform colors (from CSS variables)
const PLATFORM_COLORS = {
  blue: { top: 0x4f6494, side: 0x7289b4, border: 0x83a1ce },
  red: { top: 0x843138, side: 0x9b3938, border: 0xcf9089 },
};

// Platform dimensions
const PLATFORM_WIDTH = 72;
const PLATFORM_HEIGHT = 48;
const PLATFORM_THICKNESS = 12;
const PLATFORM_Y_OFFSET = 16; // Below pokemon feet

const BATTLE_GRID_WIDTH = GRID_WIDTH * gridTileSize;
const BATTLE_GRID_HEIGHT = GRID_HEIGHT * gridTileSize;

// Map image dimensions (fixed size)
const MAP_WIDTH = 480;
const MAP_HEIGHT = 960;
const MAP_CENTER_X = MAP_WIDTH / 2;
const MAP_CENTER_Y = MAP_HEIGHT / 2;

import { CompleteBattleLog } from "types/battle-engine";
import { isXsScreen } from "@/utils/screen-size-check";

const COLLISION_DATA = import.meta.glob(
  "../../../../game/maps/collision/**/*.json",
);

export class DuelDraftScene {
  private app: Application;
  private world: Container;
  public container: Container;
  private mapContainer: Container;
  private platformContainer: Container;
  private pokemonContainer: Container;
  private uiContainer: Container;

  private pokemons: Map<
    number,
    {
      clientPokemon: ClientPokemon;
      hitArea: Sprite;
      highlight: Graphics;
      platform: Container;
      typeDisplay: Container;
      data: DuelPokemon;
      team: "A" | "B";
      pixelX: number;
      pixelY: number;
      gridX: number;
      gridY: number;
    }
  > = new Map();

  private walkablePositions: { x: number; y: number }[] = [];
  private onPokemonClick: ((pokemonId: number) => void) | null = null;

  // Track active VFX per pokemon
  private activeVFX: Map<number, { type: string; vfx: VFX }> = new Map();
  // Track last status to detect changes
  private lastStatus: Map<number, "idle" | "selected" | "picked" | "banned"> =
    new Map();

  constructor(app: Application, world: Container) {
    this.app = app;
    this.world = world;

    this.container = new Container();
    this.container.sortableChildren = true;
    this.world.addChild(this.container);

    this.mapContainer = new Container();
    this.mapContainer.alpha = 0.3; // Hidden during draft
    this.container.addChild(this.mapContainer);

    //this.renderDebugGrid();

    this.platformContainer = new Container();
    this.platformContainer.zIndex = 8;
    this.platformContainer.sortableChildren = true;
    this.container.addChild(this.platformContainer);

    this.pokemonContainer = new Container();
    this.pokemonContainer.zIndex = 10;
    this.pokemonContainer.sortableChildren = true;
    this.container.addChild(this.pokemonContainer);

    this.uiContainer = new Container();
    this.uiContainer.zIndex = 20;
    this.container.addChild(this.uiContainer);
  }

  public setOnPokemonClick(callback: (pokemonId: number) => void) {
    this.onPokemonClick = callback;
  }

  public getPokemon(id: number): DuelPokemon | undefined {
    return this.pokemons.get(id)?.data;
  }

  public getPokemonTeam(id: number): "A" | "B" | undefined {
    return this.pokemons.get(id)?.team;
  }

  public async initialize(
    mapId: string,
    teamA: DuelPokemon[],
    teamB: DuelPokemon[],
  ) {
    await this.loadMap(mapId);
    await Promise.all([this.spawnTeam(teamA, "A"), this.spawnTeam(teamB, "B")]);
  }

  private async loadMap(mapId: string) {
    try {
      const collisionPath = `../../../../game/maps/collision/${mapId}.json`;
      const importer = COLLISION_DATA[collisionPath];

      if (importer) {
        const module: any = await importer();
        const collisionData = module.default || module;

        if (collisionData?.grid?.walkablePositions) {
          this.walkablePositions = collisionData.grid.walkablePositions;
        }
      } else {
        console.warn(
          `Collision data not found for mapId: ${mapId} at path: ${collisionPath}`,
        );
      }
    } catch (e) {
      console.warn(`Failed to load collision data`, e);
    }

    try {
      const texture = await Assets.load(`/assets/maps/${mapId}.png`);
      const mapSprite = new Sprite(texture);
      mapSprite.anchor.set(0.5);
      mapSprite.position.set(MAP_CENTER_X, MAP_CENTER_Y);
      this.mapContainer.addChild(mapSprite);
    } catch (e) {
      console.warn(`Failed to load map`, e);
    }

    try {
      const overlay = new Sprite(
        await Assets.load("/assets/maps/overlay/default.png"),
      );
      overlay.anchor.set(0.5);
      overlay.position.set(MAP_CENTER_X, MAP_CENTER_Y);
      overlay.zIndex = 999;
      this.mapContainer.addChild(overlay);
    } catch (e) { }
  }

  private async spawnTeam(team: DuelPokemon[], side: "A" | "B") {
    const isTeamA = side === "A";
    const direction = isTeamA ? Direction.N : Direction.S;
    const idealCols = [2, 3, 4, 5];

    const candidates = this.walkablePositions.filter((pos) =>
      isTeamA ? pos.y >= GRID_HEIGHT / 2 : pos.y < GRID_HEIGHT / 2,
    );

    const targetRows = Array.from(new Set(candidates.map((p) => p.y)))
      .sort((a, b) => (isTeamA ? b - a : a - b))
      .slice(0, 2);

    // Calculate dynamic Y offset for top team based on walkable positions
    const minWalkableY = Math.min(...candidates.map((p) => p.y));
    const extraOffset = 48
    const topTeamBaseOffset = isTeamA
      ? -32
      : Math.max(-48, 20 - minWalkableY * 8) + extraOffset;

    // Load all pokemons in parallel
    const loadPromises = team.map(async (pokemon, i) => {
      if (!pokemon) return null;

      const rowIndex = Math.floor(i / 4);
      const colIndex = i % 4;
      const idealX = idealCols[colIndex] ?? (isTeamA ? 3 : 2);

      let gridX = idealX;
      let gridY =
        (isTeamA ? [GRID_HEIGHT - 1, GRID_HEIGHT - 2] : [0, 1])[rowIndex] ?? 0;

      if (rowIndex < targetRows.length) {
        const rowY = targetRows[rowIndex];
        const rowCandidates = candidates.filter((p) => p.y === rowY);
        if (rowCandidates.length > 0) {
          const best = rowCandidates.reduce((prev, curr) =>
            Math.abs(curr.x - idealX) < Math.abs(prev.x - idealX) ? curr : prev,
          );
          gridX = best.x;
          gridY = best.y;
        }
      }

      const basePixelX = gridMarginX + gridX * gridTileSize + gridTileSize / 2;
      const basePixelY = gridMarginY + gridY * gridTileSize + gridTileSize / 2;
      const colOffset = (colIndex - 1.5) * DRAFT_GAP_OFFSET;
      // Push teams away from center: Team A (bottom) goes down (+), Team B (top) goes up (-)
      const verticalSpread = isTeamA
        ? DRAFT_VERTICAL_SPREAD
        : -DRAFT_VERTICAL_SPREAD;
      const rowOffset =
        (isTeamA ? -32 : topTeamBaseOffset) +
        rowIndex * DRAFT_GAP_OFFSET * (isTeamA ? -1 : 1) +
        verticalSpread;
      const pixelX = basePixelX + colOffset;
      const pixelY = basePixelY + rowOffset;

      const clientPokemon = new ClientPokemon(
        {
          ...pokemon,
          team: side,
          current_hp: 100,
          max_hp: 100,
          current_mana: 0,
          max_mana: 100,
          stats: {},
          skills: [],
          buffs: [],
          active_buffs: [],
        } as any,
        { x: gridX, y: gridY },
        side,
        this.app,
        this.pokemonContainer,
        false,
        true,
      );

      await clientPokemon.load();

      clientPokemon.Position.x = pixelX;
      clientPokemon.Position.y = pixelY;
      clientPokemon.setAnimation(["Idle"], direction);

      return { pokemon, clientPokemon, pixelX, pixelY, gridX, gridY };
    });

    const results = await Promise.all(loadPromises);

    // Register all loaded pokemons
    for (const result of results) {
      if (!result) continue;
      const { pokemon, clientPokemon, pixelX, pixelY, gridX, gridY } = result;

      const hitArea = new Sprite(Texture.WHITE);
      hitArea.width = gridTileSize;
      hitArea.height = gridTileSize * 1.5;
      hitArea.anchor.set(0.5, 0.75);
      hitArea.position.set(pixelX, pixelY);
      hitArea.alpha = 0;
      hitArea.eventMode = "static";
      hitArea.cursor = "pointer";
      hitArea.on("pointerdown", () => this.onPokemonClick?.(pokemon.id));
      this.uiContainer.addChild(hitArea);

      const highlight = new Graphics();
      highlight.position.set(pixelX, pixelY);
      highlight.visible = false;
      this.uiContainer.addChild(highlight);

      // Create summoning platform below pokemon
      const platform = this.createPlatform(side, pixelX, pixelY);
      this.platformContainer.addChild(platform);

      // Create type display above pokemon
      const typeDisplay = await this.createTypeDisplay(
        clientPokemon,
        pixelX,
        pixelY,
      );
      this.uiContainer.addChild(typeDisplay);

      this.pokemons.set(pokemon.id, {
        clientPokemon,
        hitArea,
        highlight,
        platform,
        typeDisplay,
        data: pokemon,
        team: side,
        pixelX,
        pixelY,
        gridX,
        gridY,
      });
    }
  }

  private createPlatform(team: "A" | "B", x: number, y: number): Container {
    const colors = team === "A" ? PLATFORM_COLORS.blue : PLATFORM_COLORS.red;
    const container = new Container();
    container.position.set(x, y + PLATFORM_Y_OFFSET);
    container.zIndex = y;

    // Shadow (ellipse below platform)
    const shadow = new Graphics();
    shadow.ellipse(0, PLATFORM_THICKNESS + 4, PLATFORM_WIDTH / 2 + 8, 16);
    shadow.fill({ color: 0x000000, alpha: 0.4 });
    shadow.filters = [new BlurFilter({ strength: 4 })];
    container.addChild(shadow);

    // Front face (platform thickness)
    const frontFace = new Graphics();
    frontFace.roundRect(
      -PLATFORM_WIDTH / 2,
      0,
      PLATFORM_WIDTH,
      PLATFORM_THICKNESS,
      2,
    );
    frontFace.fill({ color: colors.side });
    frontFace.stroke({ width: 2, color: 0x000000, alpha: 0.2 });
    container.addChild(frontFace);

    // Top surface
    const topSurface = new Graphics();
    topSurface.roundRect(
      -PLATFORM_WIDTH / 2,
      -PLATFORM_HEIGHT / 2,
      PLATFORM_WIDTH,
      PLATFORM_HEIGHT,
      4,
    );
    topSurface.fill({ color: colors.top });
    topSurface.stroke({ width: 3, color: colors.border });
    container.addChild(topSurface);

    // Inner grid pattern overlay
    const pattern = new Graphics();
    const patternSize = 8;
    for (
      let px = -PLATFORM_WIDTH / 2 + 4;
      px < PLATFORM_WIDTH / 2 - 4;
      px += patternSize
    ) {
      for (
        let py = -PLATFORM_HEIGHT / 2 + 4;
        py < PLATFORM_HEIGHT / 2 - 4;
        py += patternSize
      ) {
        if (
          (Math.floor(px / patternSize) + Math.floor(py / patternSize)) % 2 ===
          0
        ) {
          pattern.rect(px, py, patternSize - 1, patternSize - 1);
        }
      }
    }
    pattern.fill({ color: 0x000000, alpha: 0.15 });
    container.addChild(pattern);

    // Center marker
    const centerMarker = new Graphics();
    centerMarker.roundRect(-24, -12, 48, 24, 2);
    centerMarker.stroke({ width: 2, color: 0x000000, alpha: 0.3 });
    container.addChild(centerMarker);

    // Store references for status updates
    (container as any)._topSurface = topSurface;
    (container as any)._frontFace = frontFace;
    (container as any)._colors = colors;

    return container;
  }

  private async createTypeDisplay(
    clientPokemon: ClientPokemon,
    x: number,
    y: number,
  ): Promise<Container> {
    const container = new Container();
    container.position.set(x, y);
    container.zIndex = 15; // Above platforms but below UI
    container.sortableChildren = true;

    // Get pokemon types from the client pokemon data
    const pokemonTypes =
      (clientPokemon).PokemonData?.pokemon_types || [];

    if (pokemonTypes.length === 0) {
      return container; // No types to display
    }

    // Create type badges
    const badgeWidth = 60;
    const badgeHeight = 20;
    const badgeSpacing = 4;
    const totalWidth =
      pokemonTypes.length * badgeWidth +
      (pokemonTypes.length - 1) * badgeSpacing;

    pokemonTypes.forEach((type, index) => {
      const badgeX =
        -totalWidth / 2 + index * (badgeWidth + badgeSpacing) + badgeWidth / 2;

      // Create badge background
      const badgeBg = new Graphics();
      badgeBg.roundRect(
        -badgeWidth / 2,
        -badgeHeight / 2,
        badgeWidth,
        badgeHeight,
        4,
      );
      badgeBg.fill(this.getTypeColor(type));
      badgeBg.stroke({ width: 2, color: 0xffffff, alpha: 0.6 });

      // Create type text
      const typeText = new Text({
        text: type.toUpperCase(),
        style: new TextStyle({
          fontFamily: "Arial",
          fontSize: 10,
          fill: 0xffffff,
          fontWeight: "bold",
          align: "center",
        }),
      });
      typeText.anchor.set(0.5, 0.5);

      // Create badge container
      const badgeContainer = new Container();
      badgeContainer.position.set(badgeX, -45); // Position above pokemon
      badgeContainer.addChild(badgeBg);
      badgeContainer.addChild(typeText);

      container.addChild(badgeContainer);
    });

    return container;
  }

  private getTypeColor(type: PokemonType): number {
    // Type colors based on the CSS variables used in PokemonTypeBadge
    const colorMap: Record<PokemonType, number> = {
      [PokemonType.FIRE]: 0xff6b35, // pk-red-5
      [PokemonType.WATER]: 0x3b82f6, // pk-blue-5
      [PokemonType.GRASS]: 0x22c55e, // pk-green-4
      [PokemonType.ELECTRIC]: 0xf59e0b, // pk-yellow-3
      [PokemonType.ICE]: 0x60a5fa, // pk-blue-4
      [PokemonType.FIGHTING]: 0xef4444, // pk-red-4
      [PokemonType.POISON]: 0xa855f7, // pk-purple-4
      [PokemonType.GROUND]: 0xd97706, // pk-ground
      [PokemonType.FLYING]: 0x3b82f6, // pk-blue-5
      [PokemonType.PSYCHIC]: 0xec4899, // pk-garnet-4
      [PokemonType.BUG]: 0x84cc16, // pk-green-5
      [PokemonType.ROCK]: 0x9ca3af, // pk-gray-4
      [PokemonType.DRAGON]: 0xa855f7, // pk-purple-4
      [PokemonType.STEEL]: 0x60a5fa, // pk-blue-4
      [PokemonType.FAIRY]: 0xec4899, // pk-garnet-4
      [PokemonType.DARK]: 0x6b7280, // pk-gray-2
      [PokemonType.GHOST]: 0xf472b6, // pk-pink-4
      [PokemonType.NORMAL]: 0xd1d5db, // pk-gray-5
    };

    return colorMap[type] || 0xd1d5db;
  }

  private updatePlatformVisual(
    platform: Container,
    status: "idle" | "selected" | "picked" | "banned",
    statusColor: number,
  ) {
    const topSurface = (platform as any)._topSurface as Graphics;
    const frontFace = (platform as any)._frontFace as Graphics;
    const colors = (platform as any)._colors as {
      top: number;
      side: number;
      border: number;
    };

    if (
      !topSurface ||
      !frontFace ||
      topSurface.destroyed ||
      frontFace.destroyed
    )
      return;

    // Determine top surface color based on status
    let topColor = colors.top;
    let borderColor = colors.border;
    let sideBrightness = 0.5;

    if (status === "selected") {
      topColor = statusColor; // Yellow
      borderColor = 0xffff88;
      sideBrightness = 0.8;
    } else if (status === "picked") {
      topColor = statusColor; // Blue or Red depending on team
      borderColor = 0xffffff;
      sideBrightness = 1.0;
    } else if (status === "banned") {
      topColor = 0x444444;
      borderColor = 0x666666;
      sideBrightness = 0.3;
    }

    // Apply brightness to side
    const applyBrightness = (color: number, factor: number): number => {
      const r = Math.min(255, Math.floor(((color >> 16) & 0xff) * factor));
      const g = Math.min(255, Math.floor(((color >> 8) & 0xff) * factor));
      const b = Math.min(255, Math.floor((color & 0xff) * factor));
      return (r << 16) | (g << 8) | b;
    };

    // Redraw top surface with status color
    topSurface.clear();
    topSurface.roundRect(
      -PLATFORM_WIDTH / 2,
      -PLATFORM_HEIGHT / 2,
      PLATFORM_WIDTH,
      PLATFORM_HEIGHT,
      4,
    );
    topSurface.fill({ color: topColor });
    topSurface.stroke({ width: 3, color: borderColor });

    // Redraw front face
    frontFace.clear();
    frontFace.roundRect(
      -PLATFORM_WIDTH / 2,
      0,
      PLATFORM_WIDTH,
      PLATFORM_THICKNESS,
      2,
    );
    frontFace.fill({ color: applyBrightness(colors.side, sideBrightness) });
    frontFace.stroke({ width: 2, color: 0x000000, alpha: 0.2 });
  }

  public updateVisuals(
    selectedIds: number[],
    bannedIds: number[],
    pickedIds: number[],
    opponentPickedIds: number[],
  ) {
    this.pokemons.forEach((entry, id) => {
      const isSelected = selectedIds.includes(id);
      const isBanned = bannedIds.includes(id);
      const isPicked = pickedIds.includes(id);
      const isOpponentPicked = opponentPickedIds.includes(id);

      let status: "idle" | "selected" | "picked" | "banned" = "idle";
      let color = 0xffffff;

      if (isBanned) {
        status = "banned";
        color = 0x888888;
      } else if (isPicked) {
        status = "picked";
        color = 0x3366ff;
      } else if (isOpponentPicked) {
        status = "picked";
        color = 0xff3333;
      } else if (isSelected) {
        status = "selected";
        color = 0xffff00;
      }

      const prevStatus = this.lastStatus.get(id) ?? "idle";
      this.lastStatus.set(id, status);

      // Trigger animation when picked
      if (status === "picked" && prevStatus !== "picked") {
        const cp = entry.clientPokemon;
        const specialMelee = cp.getSpecialMeleeAnimations();
        const specialRanged = cp
          .getSpecialRangedAnimations()
          .filter((a) => a !== "Shoot" && a !== "Appeal");

        const priorityList = [
          "Pose",
          "Appeal",
          "Dance",
          ...specialMelee,
          ...specialRanged,
          "Rotate",
          "Charge",
          "Idle",
        ];

        cp.setAnimationOnce(
          priorityList,
          entry.team === "A" ? Direction.N : Direction.S,
          0,
          2,
        );
      }

      // Handle VFX transitions
      this.handleVFXTransition(
        id,
        prevStatus,
        status,
        entry.pixelX,
        entry.pixelY,
      );

      // Update pokemon visual - platform handles color now, so hide glow circles
      entry.clientPokemon.setVisualStatusNoGlow(status, color);
      entry.hitArea.eventMode = isBanned ? "none" : "static";
      entry.hitArea.cursor = isBanned ? "default" : "pointer";

      // Update platform visual with status color
      this.updatePlatformVisual(entry.platform, status, color);
    });
  }

  private handleVFXTransition(
    pokemonId: number,
    prevStatus: "idle" | "selected" | "picked" | "banned",
    newStatus: "idle" | "selected" | "picked" | "banned",
    pixelX: number,
    pixelY: number,
  ) {
    // No change
    if (prevStatus === newStatus) return;

    // Clear existing VFX if status changed
    const existingVFX = this.activeVFX.get(pokemonId);
    if (existingVFX) {
      existingVFX.vfx.destroy();
      this.activeVFX.delete(pokemonId);
    }

    // Spawn new VFX based on new status
    if (newStatus === "selected") {
      this.spawnVFX(
        pokemonId,
        VFX_SELECTED,
        pixelX,
        pixelY - 20,
        9999,
        0.5,
        1.5,
      );
    } else if (newStatus === "picked" && prevStatus !== "picked") {
      this.spawnVFX(pokemonId, VFX_PICKED, pixelX, pixelY - 10, 1, 0.4, 2.5);
    } else if (newStatus === "banned" && prevStatus !== "banned") {
      this.spawnVFX(pokemonId, VFX_BANNED, pixelX, pixelY - 10, 1, 0.5, 2);
    }
  }

  private async spawnVFX(
    pokemonId: number,
    spriteUrl: string,
    x: number,
    y: number,
    loops: number,
    speed: number,
    scale: number,
  ) {
    const vfx = new VFX({
      app: this.app,
      spriteUrl,
      spriteFrameCount: 0, // Will be read from VFX_SPRITE_FRAME_COUNT_DATA
      spriteScale: scale,
      spriteScaleX: 1,
      spriteScaleY: 1,
      spriteAlpha: 1,
      position: { x, y },
      parent: this.uiContainer,
      loops,
      speed,
      ping_pong: false,
    });

    await vfx.load();
    vfx.play();

    // Track looping VFX (for cleanup when status changes)
    if (loops > 1) {
      this.activeVFX.set(pokemonId, { type: spriteUrl, vfx });
    }
  }

  public prepareForBattle(
    battleLog: CompleteBattleLog,
  ): Record<string, ClientPokemon> {
    const participating: Record<string, ClientPokemon> = {};
    const keptIds = new Set(
      [...battleLog.teamA.members, ...battleLog.teamB.members].map(
        (m) => m.pokemon.id,
      ),
    );

    // Build a map of occupied grid positions by picked pokemon
    const pickedMap = new Map<string, boolean>();
    this.pokemons.forEach((entry, id) => {
      if (keptIds.has(id)) {
        pickedMap.set(`${entry.gridX},${entry.gridY}`, true);
      }
    });

    // Hide platforms and show map with fade transition
    this.transitionToBattle();

    this.pokemons.forEach((entry, id) => {
      if (keptIds.has(id)) {
        participating[id] = entry.clientPokemon;
        entry.hitArea.visible = false;
        entry.highlight.visible = false;
        entry.typeDisplay.visible = false; // Hide type display during battle
        // Clear draft visuals (PICKED tag, glow) and show HP bar
        entry.clientPokemon.transitionToBattle();
      } else {
        this.dismissPokemon(entry, pickedMap);
      }
    });

    this.uiContainer.removeChildren();
    return participating;
  }

  private transitionToBattle() {
    const duration = 500;
    const startTime = performance.now();

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(1, elapsed / duration);

      // Fade in map
      this.mapContainer.alpha = t;
      // Fade out platforms
      this.platformContainer.alpha = 1 - t;

      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        // Cleanup platforms after fade
        this.platformContainer.removeChildren();
      }
    };

    requestAnimationFrame(animate);
  }

  private dismissPokemon(
    entry: {
      clientPokemon: ClientPokemon;
      pixelX: number;
      pixelY: number;
      gridX: number;
      gridY: number;
      team: "A" | "B";
      hitArea: Sprite;
      highlight: Graphics;
    },
    pickedMap: Map<string, boolean>,
  ) {
    const { clientPokemon, gridX, gridY, team } = entry;

    // Cleanup UI immediately
    if (!entry.hitArea.destroyed) entry.hitArea.destroy();
    if (!entry.highlight.destroyed) entry.highlight.destroy();

    const isTeamA = team === "A";

    // Destination boundaries
    const OFF_SCREEN_TOP = gridMarginY - 200;
    const OFF_SCREEN_BOTTOM = gridMarginY + BATTLE_GRID_HEIGHT + 200;
    const OFF_SCREEN_LEFT = gridMarginX - 200;
    const OFF_SCREEN_RIGHT = gridMarginX + BATTLE_GRID_WIDTH + 200;

    // Movement Logic
    let moveDir: Direction;
    let targetX = entry.pixelX;
    let targetY = entry.pixelY;
    let goHorizontal = false;

    // Team A (Bottom): Outer is Y=13, Inner is Y=12
    // Team B (Top): Outer is Y=0, Inner is Y=1
    const isOuterRow = isTeamA ? gridY === 13 : gridY === 0;

    if (gridX === 1) {
      // Left side -> West
      goHorizontal = true;
      moveDir = Direction.W;
    } else if (gridX === 4) {
      // Right side -> East
      goHorizontal = true;
      moveDir = Direction.E;
    } else {
      // Middle columns (2, 3)
      if (isOuterRow) {
        // Outer row -> Walk vertical out
        goHorizontal = false;
        moveDir = isTeamA ? Direction.S : Direction.N;
      } else {
        // Inner row -> Check vertical neighbor
        const neighborY = isTeamA ? 13 : 0;
        const neighborKey = `${gridX},${neighborY}`;

        if (pickedMap.get(neighborKey)) {
          // Blocked by picked pokemon -> Go horizontal away from center
          goHorizontal = true;
          moveDir = gridX <= 2 ? Direction.W : Direction.E;
        } else {
          // Clear -> Walk vertical out
          goHorizontal = false;
          moveDir = isTeamA ? Direction.S : Direction.N;
        }
      }
    }

    if (goHorizontal) {
      targetX = moveDir === Direction.W ? OFF_SCREEN_LEFT : OFF_SCREEN_RIGHT;
    } else {
      targetY = moveDir === Direction.S ? OFF_SCREEN_BOTTOM : OFF_SCREEN_TOP;
    }

    // Start Animation
    clientPokemon.setAnimation(["Walk"], moveDir);

    const speed = 150; // pixels per second
    const dist = Math.sqrt(
      Math.pow(targetX - entry.pixelX, 2) + Math.pow(targetY - entry.pixelY, 2),
    );
    const duration = (dist / speed) * 1000;
    const startTime = performance.now();
    const startX = entry.pixelX;
    const startY = entry.pixelY;

    const animate = () => {
      if (clientPokemon.IsDestroyed) return;

      const now = performance.now();
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);

      clientPokemon.container.x = startX + (targetX - startX) * t;
      clientPokemon.container.y = startY + (targetY - startY) * t;
      clientPokemon.container.zIndex = clientPokemon.container.y;

      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        clientPokemon.container.destroy({ children: true });
      }
    };

    requestAnimationFrame(animate);
  }

  public destroy() {
    // Clean up all active VFX
    this.activeVFX.forEach((entry) => entry.vfx.destroy());
    this.activeVFX.clear();
    this.lastStatus.clear();

    // Clean up type displays
    this.pokemons.forEach((entry) => {
      if (!entry.typeDisplay.destroyed) {
        entry.typeDisplay.destroy({ children: true });
      }
    });

    this.container.destroy({ children: true });
  }

  private renderDebugGrid() {
    const grid = new Container();
    grid.zIndex = 5;
    this.container.addChild(grid);

    for (let x = 0; x < GRID_WIDTH; x++) {
      for (let y = 0; y < GRID_HEIGHT; y++) {
        const tile = new Graphics();
        tile.rect(0, 0, gridTileSize, gridTileSize);
        tile.fill({
          color: (x + y) % 2 === 0 ? 0xffffff : 0xcccccc,
          alpha: 0.1,
        });
        tile.stroke({ width: 1, color: 0x000000, alpha: 0.3 });
        tile.position.set(
          gridMarginX + x * gridTileSize,
          gridMarginY + y * gridTileSize,
        );
        grid.addChild(tile);
      }
    }
  }
}
